import React, { useState, useEffect } from 'react';
import {
  Button as ButtonM,
  CircularProgress,
  Container,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { CheckCircle, Lock } from '@material-ui/icons';
import { useLocation } from 'react-router-dom';
import { apiExterno } from '@services/apiExterno';
import { cpfValidation, maskCPF } from '@utils/masks';
import { AppError } from '@utils/AppError';
import { PreencherKit } from '@pages/Kit/PreencherKit';
import { PreCadastroKit } from '@pages/Kit/PreCadastroKit';

const ButtonPrimary = withStyles(() => ({
  root: {
    background: '#0D9C5B',
    color: '#FFF',
    padding: '10px 28px',
    '&:hover': { background: '#0a7d49' },
    '&:disabled': { background: '#ccc', color: '#999' }
  }
}))(ButtonM);

interface Confirmacao {
  nome_completo: string;
  cpf_colaborador: string;
  data_nascimento: string;
  email: string;
  telefone: string;
  razao_social: string;
}

interface ConsultaResponse {
  id_agendamento: number;
  status_agenda: string;
  confirmacao: Confirmacao;
  kit: any;
}

type Step = 'cpf' | 'confirmar' | 'formulario' | 'sucesso' | 'erro';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export default function KitExterno() {
  const query = useQuery();
  const token = query.get('token') ?? '';

  const [step, setStep] = useState<Step>('cpf');
  const [cpf, setCpf] = useState('');
  const [cpfValid, setCpfValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [consultaData, setConsultaData] = useState<ConsultaResponse | null>(null);
  const [antecedente, setAntecedente] = useState<any>({});
  const [genero, setGenero] = useState('');
  const [dateBirth, setDateBirth] = useState('');
  const [name, setName] = useState('');

  useEffect(() => {
    if (!token) {
      setStep('erro');
      setErrorMsg('Link inválido ou expirado. Solicite um novo link ao RH.');
    }
  }, [token]);

  function handleCpfChange(value: string) {
    const raw = value.replace(/\D/g, '');
    const masked = maskCPF(raw);
    setCpf(masked);
    setCpfValid(cpfValidation(raw));
  }

  async function handleConsultar() {
    if (!cpfValid || !token) return;
    setLoading(true);
    setErrorMsg('');
    try {
      const cpfRaw = cpf.replace(/\D/g, '');
      const res = await apiExterno.post('/ravim/colab-kit-externo', {
        token,
        cpf_colaborador: cpfRaw
      });
      const data: ConsultaResponse = res.data;
      setConsultaData(data);
      if (data.kit) setAntecedente(data.kit);
      setGenero((data.confirmacao as any).genero ?? '');
      setStep('confirmar');
    } catch (error) {
      if (error instanceof AppError) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg('Não foi possível validar seus dados. Verifique o CPF e tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function fetchSendKit() {
    if (!consultaData) return;
    try {
      const cpfRaw = cpf.replace(/\D/g, '');
      await apiExterno.post('/ravim/kit-externo', {
        token,
        cpf_colaborador: cpfRaw,
        id_agendamento: consultaData.id_agendamento,
        ...antecedente,
        nome_colaborador: antecedente.nome_colaborador || name,
        nascimento: dateBirth || antecedente.nascimento
      });
      setStep('sucesso');
    } catch (error) {
      if (error instanceof AppError) {
        setErrorMsg(error.message);
      } else {
        setErrorMsg('Erro ao salvar o formulário. Tente novamente.');
      }
    }
  }

  function handleSelectedValueRadio(field: string, value: string) {
    setAntecedente((prev: any) => ({ ...prev, [field]: value }));
  }

  if (step === 'erro') {
    return (
      <Container maxWidth="sm" style={{ marginTop: 80 }}>
        <Paper style={{ padding: 40, textAlign: 'center' }}>
          <Lock style={{ fontSize: 56, color: '#e53935', marginBottom: 16 }} />
          <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
            Acesso negado
          </Typography>
          <Typography color="textSecondary">{errorMsg}</Typography>
        </Paper>
      </Container>
    );
  }

  if (step === 'cpf') {
    return (
      <Container maxWidth="sm" style={{ marginTop: 80 }}>
        <Paper style={{ padding: 40 }}>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            style={{ fontWeight: 'bold', color: '#0D9C5B' }}
          >
            Ficha Clínica
          </Typography>
          <Typography align="center" color="textSecondary" style={{ marginBottom: 32 }}>
            Para acessar seu formulário, informe seu CPF.
          </Typography>
          <TextField
            label="CPF"
            variant="outlined"
            fullWidth
            value={cpf}
            onChange={(e) => handleCpfChange(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleConsultar(); }}
            inputProps={{ maxLength: 14 }}
            style={{ marginBottom: 24 }}
          />
          {errorMsg && (
            <Typography color="error" style={{ marginBottom: 16 }}>
              {errorMsg}
            </Typography>
          )}
          <ButtonPrimary
            fullWidth
            disabled={!cpfValid || loading}
            onClick={handleConsultar}
          >
            {loading ? <CircularProgress size={22} style={{ color: '#fff' }} /> : 'Confirmar'}
          </ButtonPrimary>
        </Paper>
      </Container>
    );
  }

  if (step === 'confirmar' && consultaData) {
    const c = consultaData.confirmacao;
    return (
      <Container maxWidth="sm" style={{ marginTop: 80 }}>
        <Paper style={{ padding: 40 }}>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            style={{ fontWeight: 'bold', color: '#0D9C5B' }}
          >
            Confirme seus dados
          </Typography>
          <Typography align="center" color="textSecondary" style={{ marginBottom: 32 }}>
            Verifique se os dados abaixo são seus antes de continuar.
          </Typography>
          <div style={{ marginBottom: 24 }}>
            {[
              { label: 'Nome', value: c.nome_completo },
              { label: 'CPF', value: c.cpf_colaborador },
              { label: 'Nascimento', value: c.data_nascimento },
              { label: 'E-mail', value: c.email },
              { label: 'Telefone', value: c.telefone },
              { label: 'Empresa', value: c.razao_social }
            ].map(({ label, value }) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  borderBottom: '1px solid #eee',
                  padding: '10px 0'
                }}
              >
                <Typography style={{ fontWeight: 600, color: '#555' }}>{label}</Typography>
                <Typography>{value || '—'}</Typography>
              </div>
            ))}
          </div>
          <ButtonPrimary fullWidth onClick={() => setStep('formulario')}>
            Meus dados estão corretos — Preencher Formulário
          </ButtonPrimary>
        </Paper>
      </Container>
    );
  }

  if (step === 'formulario' && consultaData) {
    const hasKit = consultaData.kit && Object.keys(consultaData.kit).length > 0;
    return (
      <Container>
        {hasKit ? (
          <PreencherKit
            genero={genero}
            user={[]}
            fetchSendKit={fetchSendKit}
            handleSelectedValueRadio={handleSelectedValueRadio}
            value={antecedente}
          />
        ) : (
          <PreCadastroKit
            genero={genero}
            user={[]}
            fetchSendKit={fetchSendKit}
            handleSelectedValueRadio={handleSelectedValueRadio}
            cpf={cpf}
            handleCpf={() => {}}
            handleNomeCompleto={(v: string) => setName(v)}
            handleNomeDataDeNascimento={(v: string) => setDateBirth(v)}
            value={antecedente}
          />
        )}
        {errorMsg && (
          <Typography color="error" align="center" style={{ marginTop: 16 }}>
            {errorMsg}
          </Typography>
        )}
      </Container>
    );
  }

  if (step === 'sucesso') {
    return (
      <Container maxWidth="sm" style={{ marginTop: 80 }}>
        <Paper style={{ padding: 40, textAlign: 'center' }}>
          <CheckCircle style={{ fontSize: 72, color: '#0D9C5B', marginBottom: 16 }} />
          <Typography variant="h5" gutterBottom style={{ fontWeight: 'bold' }}>
            Formulário enviado com sucesso!
          </Typography>
          <Typography color="textSecondary">
            Seus dados foram salvos. Apresente este comprovante no dia do seu exame.
          </Typography>
        </Paper>
      </Container>
    );
  }

  return null;
}
